import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useLinkedTickets } from '../../../hooks/useLinkedTickets';
import { LinkedTicketChips } from '../../tickets/LinkedTicketChips';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Divider,
  Link,
  Alert,
  TextField,
  Tooltip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddIcon from '@mui/icons-material/Add';
import PlaceIcon from '@mui/icons-material/Place';
import SmsIcon from '@mui/icons-material/Sms';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { IconPencil } from '@tabler/icons-react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { createQuote, fetchQuotes } from '../../../services/quotesApi';
import { formatQuoteServiceTotal } from '../../../utils/quoteServiceTotal';
import type { QuotesListItem, QuoteStatus, QuoteType } from '@techgitter/contracts';
import type { WorkItem } from '../workItemTypes';
import type { TaskListDefinition, LineItemSyncResponse, LineItemsSyncedPayload, RepairshoprInvoice, ResolvedPricingProfile, WorkItemInvoiceSummary, JobLogType, PartOrderLine, CheckInDraftJobSummary } from '@techgitter/contracts';
import { useLineItemsSyncRanAt } from '../../../services/lineItemsSyncStatus';
import { TicketInvoicePrompt } from '../../invoices/TicketInvoicePrompt';
import { resolvePricingProfile } from '@techgitter/contracts';
import { ChecklistPanel } from '../../tickets/ChecklistPanel';
import { LineItemCard } from '../../tickets/LineItemCard';
import { JobLogPanel } from './JobLogPanel';
import { NoDiagnosticsQuoteDialog } from './NoDiagnosticsQuoteDialog';
import { PartsPanel } from '../PartsPanel';
import { mergeLogsWithComments } from '../utils/mergeLogsWithComments';
import { hasDiagnosticsLog } from '../utils/diagnosticsLog';
import { useJobLogs } from '../../../hooks/useJobLogs';
import { useWorkItemChecklist } from '../../../hooks/useWorkItemChecklist';
import { useWorkItemLineItems } from '../../../hooks/useWorkItemLineItems';
import { useSignalREvent } from '../../../hooks/useSignalR';
import { useTaskLists } from '../../../hooks/useTaskLists';
import { useTaskDropdowns } from '../../../hooks/useTaskDropdowns';
import { useProductCatalog } from '../../../hooks/useProductCatalog';
import { usePricingProfilesConfig } from '../../../hooks/usePricingProfilesConfig';
import { useCustomerServices } from '../../../hooks/useCustomerServices';
import { AddCustomerServiceModal } from '../../tickets/Customer/AddCustomerServiceModal';
import { useCustomerNotes } from '../../../hooks/useCustomerNotes';
import { useCustomerAliases } from '../../../hooks/useCustomerAliases';
import { useCustomerHasMonthlyInvoiceSchedule } from '../../../hooks/useCustomerHasMonthlyInvoiceSchedule';
import { useInvoiceCreation } from '../../../hooks/useInvoiceCreation';
import { AddEditAliasModal } from '../../customers/AddEditAliasModal';
import { getVendors } from '../../../services/vendorService';
import { usePublicHolidays } from '../../../hooks/usePublicHolidays';
import { useAfterHoursSuggestion } from '../../../hooks/useAfterHoursSuggestion';
import { useCheckInDeviceIdentities, formatDeviceIdentityParts } from '../../../hooks/useCheckInDeviceIdentities';
import { CorrectSerialDialog } from '../../check-in/device/CorrectSerialDialog';
import { getCustomerIndexCached } from '../../../services/customerIndexCacheService';
import { fetchJobInvoices } from '../../../services/jobLineItemsSyncService';
import { runEntityResync } from '../../../services/rsSyncService';
import { unlinkAppointmentFromJob } from '../../../services/workItemDataService';
import { resolveStandaloneTaskList } from '../../../utils/checklistEngine';
import {
  collectReferencedContextKeys,
  getMissingContextKeys,
  type MissingContextKeys,
} from '../../../utils/taskListContextRequirements';
import { TaskListContextPromptDialog, type TaskListContextValues } from '../../tickets/TaskListContextPromptDialog';
import type { LiveChecklist } from '../../../types/taskList';
import { formatAppointmentDateTime, formatCustomerDisplay } from '../utils/workItemUtils';

const QUOTE_STATUS_COLOR: Record<
  QuoteStatus,
  'default' | 'primary' | 'info' | 'success' | 'error' | 'warning'
> = {
  requested: 'primary',
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  declined: 'error',
  expired: 'warning',
};

const QUOTE_TYPE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair',
  network_install: 'Network Install',
  m365: 'M365',
  replacement: 'New Computer',
};

interface WorkItemExpansionPanelProps {
  workItem: WorkItem;
  userName: string;
  isAdmin?: boolean;
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
  onDelete?: (workItem: WorkItem) => Promise<void>;
  /** Navigate to the Check-In Session detail page. Only set when the
   * surrounding view (Work Item Dashboard) knows how to handle it. */
  onOpenCheckInSession?: (sessionId: string) => void;
  partLines?: PartOrderLine[];
}

/**
 * Pick the next composite-ID suffix for a task list that may already
 * be attached. Reads the existing checklist for sections whose
 * `sourceTaskListId` matches and returns one past the highest `--N`
 * suffix found (or 0 if the list isn't attached yet). Keeps section /
 * task IDs unique when the same task list is attached more than once.
 */
function nextAttachmentIndex(
  checklist: LiveChecklist | null,
  taskListId: string,
): number {
  if (!checklist) return 0;
  let max = -1;
  for (const s of checklist.sections) {
    if (s.sourceTaskListId !== taskListId) continue;
    const parts = s.sectionId.split('--');
    const suffix = parts.length > 1 ? Number(parts[parts.length - 1]) : 0;
    if (Number.isFinite(suffix) && suffix > max) max = suffix;
  }
  return max + 1;
}

/** Segmented container for the RepairShopr shortcut icons — one outer border
 *  with internal dividers, like the invoice header's external-links group. */
const shortcutGroupSx = {
  display: 'inline-flex',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  '& > span': {
    display: 'inline-flex',
    borderRight: 1,
    borderColor: 'divider',
  },
  '& > span:last-of-type': { borderRight: 0 },
};

/** Individual shortcut icon button — square, borderless (the group draws the
 *  border), with a light hover tint. */
const rsShortcutSx = {
  width: 32,
  height: 32,
  borderRadius: 0,
  transition: 'background-color 0.2s ease',
  '&:hover:not(:disabled)': {
    bgcolor: 'rgba(25, 118, 210, 0.08)',
  },
};

export const WorkItemExpansionPanel: React.FC<WorkItemExpansionPanelProps> = ({
  workItem,
  userName,
  isAdmin,
  onWorkItemUpdated,
  onDelete,
  onOpenCheckInSession,
  partLines,
}) => {
  // Logs bind to a job when there is one, and to the appointment itself only for
  // standalone appointments (no linked job). Line items / checklist stay
  // job-bound via `jobGuid` below.
  const logLinkedJobGuid = workItem.jobGUID ?? workItem.linkedJobGuid;
  const isStandaloneAppointment = workItem.type === 'appointment' && !logLinkedJobGuid;
  const { logs, isLoading: logsLoading, error: logsError, isSubmitting, addLog } = useJobLogs(
    isStandaloneAppointment ? workItem.guid : (logLinkedJobGuid ?? workItem.guid),
    userName,
    isStandaloneAppointment ? 'appointment' : 'job',
  );

  const displayEntries = useMemo(
    () => mergeLogsWithComments(logs, workItem.recentComments),
    [logs, workItem.recentComments],
  );

  // Per-device make/serial — fetched from the full session (the work-item
  // summary only carries device type + inspection status). Populated for
  // check-in rows and for jobs converted from a check-in.
  const {
    identities: deviceIdentities,
    draftJobs: fullCheckInDraftJobs,
    refresh: refreshCheckInSession,
  } = useCheckInDeviceIdentities(workItem);
  // Device whose serial is being corrected (held by id so the dialog reads the
  // freshest copy after a refetch). Same dialog as the v2 technician card.
  const [correctingSerialJobId, setCorrectingSerialJobId] = useState<string | null>(null);
  const correctingSerialJob = correctingSerialJobId
    ? fullCheckInDraftJobs[correctingSerialJobId]
    : undefined;

  // One device row (type + summary + RS ticket + inspection + charge, with a
  // make/serial line beneath). Shared by the Check-In section and the Device
  // section shown on jobs converted from a check-in.
  const renderCheckInDeviceRow = (dj: CheckInDraftJobSummary) => {
    const identityParts = formatDeviceIdentityParts(deviceIdentities[dj.id], dj.deviceType);
    return (
      <Paper key={dj.id} variant="outlined" sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Chip label={dj.deviceType.replace(/_/g, ' ')} size="small" variant="outlined" />
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dj.serviceSummary}
            </Typography>
            {(dj.repairShoprTicketNumber || dj.repairShoprTicketId) && (
              <Chip
                label={`RS #${dj.repairShoprTicketNumber ?? dj.repairShoprTicketId}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Chip
              label={dj.inspectionStatus.replace(/_/g, ' ')}
              size="small"
              color={dj.inspectionStatus === 'complete' ? 'success' : 'default'}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              {(dj.chargesTotal ?? 0).toLocaleString(undefined, { style: 'currency', currency: 'AUD' })}
            </Typography>
          </Box>
        </Box>
        {(identityParts.length > 0 || fullCheckInDraftJobs[dj.id]?.inspection) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {identityParts.join(' · ')}
            </Typography>
            {/* Only once the session has loaded and the device was inspected —
                there is no serial to correct before that. */}
            {fullCheckInDraftJobs[dj.id]?.inspection && (
              <Tooltip title={`Correct ${dj.deviceType === 'phone' ? 'IMEI' : 'serial'}`}>
                <IconButton
                  size="small"
                  onClick={() => setCorrectingSerialJobId(dj.id)}
                  aria-label={`Correct ${dj.deviceType === 'phone' ? 'IMEI' : 'serial'}`}
                  sx={{ p: 0.25, color: 'text.disabled' }}
                >
                  <IconPencil size={13} stroke={1.5} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const handleChecklistSaved = useCallback(
    (savedChecklist: NonNullable<WorkItem['taskList']>, savedAudit: NonNullable<WorkItem['taskAudit']>) => {
      onWorkItemUpdated(workItem.id, { taskList: savedChecklist, taskAudit: savedAudit });
    },
    [onWorkItemUpdated, workItem.id],
  );

  const { dropdownCatalog } = useTaskDropdowns();
  const {
    checklist,
    progress,
    isDirty,
    isSaving,
    saveError,
    appendChecklist,
    onSetStatus,
    onSetNote,
    onSetOption,
    onRemoveTask,
    onRestoreTask,
    onRemoveSection,
    onRestoreSection,
    onAddManualTask,
    onSave,
  } = useWorkItemChecklist({
    jobGuid: workItem.jobGUID ?? workItem.guid,
    savedChecklist: workItem.taskList,
    savedAudit: workItem.taskAudit,
    userName,
    taskDropdowns: dropdownCatalog.dropdowns,
    onSaved: handleChecklistSaved,
  });

  // Task list picker
  const { taskListCatalog, isLoading: isLoadingTaskLists } = useTaskLists();
  const [showTaskListPicker, setShowTaskListPicker] = useState(false);
  const [checklistExpanded, setChecklistExpanded] = useState(true);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(true);
  // Job → job links (the ticket form's Link button). The children are fetched
  // here, when the card is expanded, never on the dashboard list.
  const { children: linkedTickets, isLoading: isLoadingLinkedTickets } = useLinkedTickets(
    workItem.type === 'job' ? (workItem.jobGUID ?? workItem.guid) : null,
  );
  const hasAnyLinkedItem =
    (workItem.type === 'job' && !!workItem.linkedFromJobGuid) ||
    (workItem.type === 'job' && !!workItem.appointments && workItem.appointments.length > 0) ||
    ((workItem.type === 'appointment' || workItem.type === 'job-appointment') && !!workItem.linkedJobGuid) ||
    (!!partLines && partLines.length > 0);
  const [linkedItemsExpanded, setLinkedItemsExpanded] = useState(hasAnyLinkedItem);
  // Children arrive after the first render, so open the section once they do.
  const hasLinkedTickets = linkedTickets.length > 0;
  useEffect(() => {
    if (hasLinkedTickets) setLinkedItemsExpanded(true);
  }, [hasLinkedTickets]);
  const [unlinkingAppointment, setUnlinkingAppointment] = useState<string | null>(null);

  const [pendingTaskListAttach, setPendingTaskListAttach] = useState<{
    def: TaskListDefinition;
    missing: MissingContextKeys;
  } | null>(null);

  const attachResolvedTaskList = useCallback((
    taskListDef: TaskListDefinition,
    additionalDetails: Record<string, string | number | boolean>,
    customer: Record<string, string | number | boolean>,
  ) => {
    // Pass the full catalog so any nested TaskListReferences inside
    // taskListDef can be expanded by the resolver (composable task
    // lists).
    const attachmentIndex = nextAttachmentIndex(checklist, taskListDef.taskListId);
    const sections = resolveStandaloneTaskList(
      taskListDef,
      attachmentIndex,
      additionalDetails,
      customer,
      taskListCatalog.taskLists,
    );
    appendChecklist({ sections });
    setShowTaskListPicker(false);
  }, [appendChecklist, taskListCatalog, checklist]);

  const handleAttachTaskList = useCallback((taskListDef: TaskListDefinition) => {
    const referenced = collectReferencedContextKeys(taskListDef, taskListCatalog.taskLists);
    const missing = getMissingContextKeys(referenced, {}, {});
    if (missing.fieldValueKeys.length === 0 && missing.customerContextKeys.length === 0) {
      attachResolvedTaskList(taskListDef, {}, {});
      return;
    }
    setPendingTaskListAttach({ def: taskListDef, missing });
  }, [attachResolvedTaskList, taskListCatalog]);

  const handleTaskListContextSubmit = useCallback((values: TaskListContextValues) => {
    if (!pendingTaskListAttach) return;
    attachResolvedTaskList(
      pendingTaskListAttach.def,
      values.fieldValues,
      values.customerContextValues,
    );
    setPendingTaskListAttach(null);
  }, [pendingTaskListAttach, attachResolvedTaskList]);

  // After-hours detection
  const { holidays } = usePublicHolidays();
  const afterHoursSuggestion = useAfterHoursSuggestion(holidays);
  const afterHoursContext = useMemo(() =>
    afterHoursSuggestion.isAfterHours
      ? { isAfterHours: true as const, window: afterHoursSuggestion.window, reason: afterHoursSuggestion.reason }
      : undefined,
    [afterHoursSuggestion]
  );

  // Customer data hooks
  const dataverseCustomerId = workItem.customerId || null;
  const {
    services: customerServices,
    isLoading: isLoadingServices,
    create: createService,
  } = useCustomerServices(dataverseCustomerId);

  const {
    notes: customerNotes,
    isLoading: isLoadingNotes,
  } = useCustomerNotes(dataverseCustomerId);

  const {
    aliases: customerAliases,
    isLoading: isLoadingAliases,
    create: createAlias,
  } = useCustomerAliases(dataverseCustomerId);

  const { hasMonthlyInvoiceSchedule } = useCustomerHasMonthlyInvoiceSchedule(
    dataverseCustomerId,
  );

  // Add service modal (admin only)
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);

  // Add alias modal
  const [addAliasModalOpen, setAddAliasModalOpen] = useState(false);
  const [vendors, setVendors] = useState<import('@techgitter/contracts').Vendor[]>([]);
  useEffect(() => {
    if (!addAliasModalOpen) return;
    const controller = new AbortController();
    getVendors(controller.signal).then(setVendors).catch(() => {});
    return () => controller.abort();
  }, [addAliasModalOpen]);

  // Line items
  const handleLineItemsSynced = useCallback(
    (response: LineItemSyncResponse) => {
      onWorkItemUpdated(workItem.id, {
        rsLineItems: response.lineItems,
        lineItemOverrides: response.overrides,
        lineItemsSyncedAt: response.syncedAt,
        lineItems: undefined,
      });
    },
    [onWorkItemUpdated, workItem.id],
  );

  // Echo-suppressed sync: ref is set before hook so schedulePendingSync can use it
  const jobGuid = workItem.jobGUID ?? workItem.guid;
  const selfSyncRef = useRef(false);
  const syncRequestedRef = useRef<() => void>(() => {});

  const {
    lineItems,
    expandedItems: lineItemExpanded,
    total: lineItemsTotal,
    isDirty: lineItemsDirty,
    isSaving: lineItemsSaving,
    saveError: lineItemsSaveError,
    onAddLineItem,
    onUpdateLineItem,
    onDeleteLineItem,
    onExpandChange: onLineItemExpandChange,
    onSave: onSaveLineItems,
    provisionLicense,
    isSyncing,
    syncError,
    lastSyncedAt,
    onSync,
    hasRsData,
    validationErrorItemId,
  } = useWorkItemLineItems({
    jobGuid,
    onSyncRequested: useCallback(() => { syncRequestedRef.current(); }, []),
    rsLineItems: workItem.rsLineItems,
    lineItemOverrides: workItem.lineItemOverrides,
    lineItemsSyncedAt: workItem.lineItemsSyncedAt,
    invoices: workItem.invoices,
    onSynced: handleLineItemsSynced,
  });

  // Listen for line item sync broadcasts (other users or orchestrator cron).
  // Use a ref to suppress the echo when we initiated the sync ourselves.
  const wrappedOnSync = useCallback(async () => {
    selfSyncRef.current = true;
    await onSync();
  }, [onSync]);
  syncRequestedRef.current = wrappedOnSync;
  useSignalREvent('jobs:line-items-synced', useCallback(
    (data: unknown) => {
      const payload = data as LineItemsSyncedPayload;
      if (payload.jobGuid === jobGuid) {
        if (selfSyncRef.current) {
          selfSyncRef.current = false;
          return;
        }
        onSync();
      }
    },
    [jobGuid, onSync],
  ));



  // RS → DV ticket sync (sm4rt-sync-rs2dv)
  const [isRsSyncing, setIsRsSyncing] = useState(false);
  const handleRsSync = useCallback(async () => {
    if (!workItem.id) return;
    setIsRsSyncing(true);
    try {
      await runEntityResync('tickets', { id: workItem.id });
    } finally {
      setIsRsSyncing(false);
    }
  }, [workItem.id]);

  // RepairShopr customer ID (resolved below in useEffect, declared early for invoice handler)
  const [rsCustomerId, setRsCustomerId] = useState<number>(0);

  // Invoice status
  const [invoices, setInvoices] = useState<(RepairshoprInvoice | WorkItemInvoiceSummary)[] | null>(
    workItem.invoices ?? null,
  );
  const [isCheckingInvoice, setIsCheckingInvoice] = useState(false);

  const hasConvertedItems = lineItems.some(li => li.converted);
  const hasUnpaidItems = lineItems.some(li => !li.converted && !li.nonChargeable);
  const hasPendingLineItems = lineItems.some(li => li.pending);
  const createInvoiceDisabledReason = isSyncing
    ? 'Syncing line items…'
    : lineItemsSaving
      ? 'Saving line items…'
      : lineItemsDirty
        ? 'Save line items before creating an invoice'
        : hasPendingLineItems
          ? 'Waiting for RepairShopr to confirm the new items — try again in a few seconds'
          : null;

  const [createInvoiceError, setCreateInvoiceError] = useState<string | null>(null);
  const [invoicePromptOpen, setInvoicePromptOpen] = useState(false);
  const invoiceNavigate = useNavigate();

  const [creatingQuote, setCreatingQuote] = useState(false);
  const [createQuoteError, setCreateQuoteError] = useState<string | null>(null);
  // Gate: warn before quoting a job with no Diagnostics job-log.
  const [noDiagnosticsPromptOpen, setNoDiagnosticsPromptOpen] = useState(false);
  // Bumped to tell the JobLogPanel to focus its input and switch to the
  // Diagnostics comment type when the tech declines the prompt.
  const [focusDiagnosticsSignal, setFocusDiagnosticsSignal] = useState(0);

  const [quotes, setQuotes] = useState<QuotesListItem[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);

  // Fetch quotes linked to this job. Re-fetches when the linked job
  // changes (e.g. after an appointment is linked to a new job). The
  // create-quote flow optimistically prepends the new quote to avoid
  // a round-trip.
  useEffect(() => {
    if (!workItem.jobGUID) {
      setQuotes([]);
      return;
    }
    const controller = new AbortController();
    setQuotesLoading(true);
    setQuotesError(null);
    fetchQuotes({
      page: 1,
      pageSize: 50,
      jobId: workItem.jobGUID,
      signal: controller.signal,
    })
      .then((resp) => {
        setQuotes(resp.items);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setQuotesError(
          err instanceof Error ? err.message : 'Failed to load quotes',
        );
      })
      .finally(() => setQuotesLoading(false));
    return () => controller.abort();
  }, [workItem.jobGUID]);

  const doCreateQuote = useCallback(async () => {
    if (creatingQuote) return;
    if (!workItem.customerId) {
      setCreateQuoteError('Ticket has no linked customer — cannot create a quote');
      return;
    }
    setCreatingQuote(true);
    setCreateQuoteError(null);
    try {
      // Repair Service quotes use the repairServices tree (labour + parts
      // nested under each customer-facing line), not the legacy items
      // array. We omit items so the backend seeds the consolidated
      // description from the latest Diagnostics job-log instead.
      // A repair quote's deviceType drives the repair-service-type catalog
      // lookup, which only covers laptop / desktop / all-in-one. Other
      // check-in device types (phone, tablet, drives) have no catalog
      // mapping, so we leave deviceType unset for them — the device's
      // inspection still flows through via sourceCheckInDraftJobId below.
      const rawDeviceType = workItem.checkInDraftJobs?.[0]?.deviceType;
      const checkInDeviceType =
        rawDeviceType === 'laptop' ||
        rawDeviceType === 'desktop' ||
        rawDeviceType === 'all_in_one'
          ? rawDeviceType
          : undefined;
      // Auto-link the quote to a specific check-in device only when the
      // work item has exactly one device. With 2+ devices we leave it
      // null so the tech consciously picks on the quote page —
      // arbitrarily defaulting to the first device would hide the
      // others' inspection notes.
      const sourceCheckInDraftJobId =
        workItem.checkInDraftJobs?.length === 1
          ? workItem.checkInDraftJobs[0].id
          : undefined;
      const result = await createQuote({
        customerId: workItem.customerId,
        sm4rtJobId: workItem.jobGUID,
        type: 'repair',
        repairServices: [],
        deviceType: checkInDeviceType,
        sourceCheckInDraftJobId,
      });
      // Prepend the new quote so it shows immediately without a refetch.
      // QuoteDetail extends QuotesListItem so the shape is compatible.
      setQuotes((prev) => [result, ...prev]);
      invoiceNavigate(`/quote/${result.number}`);
    } catch (e) {
      setCreateQuoteError(
        e instanceof Error ? e.message : 'Failed to create quote',
      );
    } finally {
      setCreatingQuote(false);
    }
  }, [creatingQuote, workItem, invoiceNavigate]);

  // Add Quote entry point: a repair quote's description is seeded from the
  // latest Diagnostics job-log, so warn first when none has been posted.
  const handleCreateQuote = useCallback(() => {
    if (creatingQuote) return;
    if (hasDiagnosticsLog(logs)) {
      void doCreateQuote();
      return;
    }
    setNoDiagnosticsPromptOpen(true);
  }, [creatingQuote, logs, doCreateQuote]);

  const handleConfirmQuoteWithoutDiagnostics = useCallback(() => {
    setNoDiagnosticsPromptOpen(false);
    void doCreateQuote();
  }, [doCreateQuote]);

  const handleDeclineQuoteWithoutDiagnostics = useCallback(() => {
    setNoDiagnosticsPromptOpen(false);
    setFocusDiagnosticsSignal((n) => n + 1);
  }, []);

  const onCreateInvoice = useCallback(() => {
    setInvoicePromptOpen(true);
  }, []);

  // Save and Take Payment — save line items, await sync, create an
  // invoice for all unconverted items, then navigate to the invoice
  // page with the Check Out (payment) dialog pre-opened.
  const [saveAndPayActive, setSaveAndPayActive] = useState(false);
  const [saveAndPayError, setSaveAndPayError] = useState<string | null>(null);
  const {
    phase: invoiceCreationPhase,
    invoiceNumber: createdInvoiceNumber,
    error: invoiceCreationError,
    submit: submitInvoiceCreation,
    reset: resetInvoiceCreation,
  } = useInvoiceCreation();

  const handleSaveAndTakePayment = useCallback(async () => {
    if (saveAndPayActive) return;
    setSaveAndPayError(null);
    resetInvoiceCreation();
    setSaveAndPayActive(true);
    try {
      await onSaveLineItems();
    } catch (err) {
      setSaveAndPayActive(false);
      setSaveAndPayError(err instanceof Error ? err.message : 'Failed to save line items');
    }
  }, [saveAndPayActive, onSaveLineItems, resetInvoiceCreation]);

  const onCheckInvoice = useCallback(async () => {
    setIsCheckingInvoice(true);
    try {
      const result = await fetchJobInvoices(jobGuid);
      setInvoices(result.invoices);
    } catch {
      // Silently fail — invoice check is non-critical
    } finally {
      setIsCheckingInvoice(false);
    }
  }, [jobGuid]);

  // Determine if any fetched invoice is paid
  const invoicePaid = useMemo(
    () => invoices?.some(inv => inv.is_paid) ?? false,
    [invoices],
  );

  // Pick the first invoice ID (paid preferred) for linking to RepairShopr
  const invoiceId = useMemo(() => {
    if (!invoices || invoices.length === 0) return undefined;
    const paid = invoices.find(inv => inv.is_paid);
    return (paid ?? invoices[0]).id;
  }, [invoices]);

  // Product catalog and pricing (needed by LineItemCard)
  const { products } = useProductCatalog();
  const { config: pricingProfilesConfig } = usePricingProfilesConfig();

  // Resolve pricing profile from cached customer index
  const [resolvedPricing, setResolvedPricing] = useState<ResolvedPricingProfile | undefined>();
  useEffect(() => {
    if (!dataverseCustomerId) return;
    let cancelled = false;
    getCustomerIndexCached().then(result => {
      if (cancelled) return;
      const match = result.items.find(c => c.dataverseCustomerId === dataverseCustomerId);
      if (match) {
        setRsCustomerId(Number(match.repairShoprCustomerId) || 0);
        if (pricingProfilesConfig && products.length > 0) {
          const resolved = resolvePricingProfile(
            pricingProfilesConfig,
            match.customerType ?? 'Business',
            match.designation ?? undefined,
            products,
          );
          setResolvedPricing(resolved);
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [dataverseCustomerId, pricingProfilesConfig, products]);

  // Save and Take Payment: once line items have fully settled (saved,
  // no pending RS confirmations, no dirty edits, not currently syncing),
  // kick off invoice creation. Guarded by saveAndPayActive so the
  // existing Create Invoice button continues to work independently.
  useEffect(() => {
    if (!saveAndPayActive) return;
    if (invoiceCreationPhase !== 'idle') return;
    if (lineItemsSaving || lineItemsDirty || hasPendingLineItems || isSyncing) return;
    if (!rsCustomerId) {
      setSaveAndPayActive(false);
      setSaveAndPayError('Customer is not linked to RepairShopr — cannot create invoice');
      return;
    }
    void submitInvoiceCreation({
      rsCustomerId,
      rsTicketId: Number(workItem.id) || 0,
      dvJobGuid: jobGuid,
    });
  }, [
    saveAndPayActive,
    invoiceCreationPhase,
    lineItemsSaving,
    lineItemsDirty,
    hasPendingLineItems,
    isSyncing,
    rsCustomerId,
    workItem.id,
    jobGuid,
    submitInvoiceCreation,
  ]);

  // Save and Take Payment: navigate to the new invoice with autoOpenCheckout
  // once the orchestrator has confirmed creation. On failure, surface error.
  useEffect(() => {
    if (!saveAndPayActive) return;
    if (invoiceCreationPhase === 'succeeded' && createdInvoiceNumber) {
      // RS auto-copies the ticket's line items into the new invoice, and
      // the orchestrator syncs them to cr001_line_items on the DV row. Do
      // not pass preselectedLineItems here — re-adding them on the
      // InvoiceDetailPage clobbers the DV column via _sync_dv_line_items.
      setSaveAndPayActive(false);
      invoiceNavigate(`/invoice/${createdInvoiceNumber}`, {
        state: { autoOpenCheckout: true },
      });
    } else if (invoiceCreationPhase === 'failed') {
      setSaveAndPayActive(false);
      setSaveAndPayError(invoiceCreationError ?? 'Failed to create invoice');
    }
  }, [
    saveAndPayActive,
    invoiceCreationPhase,
    createdInvoiceNumber,
    invoiceCreationError,
    invoiceNavigate,
  ]);

  const handleAddLog = useCallback(
    async (req: { type: JobLogType; text: string }) => {
      await addLog(req);
      onWorkItemUpdated(workItem.id, {
        lastJobLog: { text: req.text, createdAt: new Date().toISOString() },
      });
    },
    [addLog, onWorkItemUpdated, workItem.id],
  );

  // Delete work item state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = useCallback(() => {
    // Check-in sessions own their own styled cancel dialog at the dashboard
    // level (CancelSessionDialog — captures the cancellation reason), and jobs
    // theirs (JobDeleteDialog — lists what the delete takes with it). Skip
    // the generic "Delete Work Item" confirm here so only one dialog shows.
    if ((workItem.type === 'check-in' || workItem.type === 'job') && onDelete) {
      void onDelete(workItem);
      return;
    }
    setDeleteConfirmOpen(true);
  }, [workItem, onDelete]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(workItem);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, workItem]);

  const hasLinkedTicket = !!workItem.linkedTicketId;

  const syncRanAt = useLineItemsSyncRanAt();
  const relativeSyncRan = useMemo(() => {
    if (!syncRanAt) return null;
    const diffMs = Date.now() - new Date(syncRanAt).getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }, [syncRanAt]);

  const hasAppointment = workItem.appointments && workItem.appointments.length > 0;
  const todayAppointment = hasAppointment
    ? workItem.appointments!.find(a => {
        if (!a.startAt) return false;
        const start = new Date(a.startAt);
        const today = new Date();
        return (
          start.getFullYear() === today.getFullYear() &&
          start.getMonth() === today.getMonth() &&
          start.getDate() === today.getDate()
        );
      }) ?? workItem.appointments![0]
    : null;

  const customerDisplayName = formatCustomerDisplay(workItem);

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      {/* Top row: Customer (left half) + Job log (right half) */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Left: Customer info section */}
        <Box sx={{ flex: '0 0 380px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Section header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Customer Info
            </Typography>
          </Box>

          {/* Label-value rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Customer */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Customer
              </Typography>
              <Typography variant="body2">
                {workItem.customerId ? (
                  // The name opens the customer's SM4RT record. `customerId` is
                  // the Dataverse GUID, one of the two ids /customer/:id resolves.
                  <Link
                    component={RouterLink}
                    to={`/customer/${workItem.customerId}`}
                    underline="hover"
                  >
                    {customerDisplayName}
                  </Link>
                ) : (
                  customerDisplayName
                )}
              </Typography>
            </Box>

            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EmailIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Email
              </Typography>
              {workItem.customerEmail ? (
                <Tooltip title={workItem.customerEmail} placement="top">
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${workItem.customerEmail}`}
                    noWrap
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                  >
                    {workItem.customerEmail}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>

            {/* Mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneAndroidIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Mobile
              </Typography>
              {workItem.customerMobile ? (
                <Typography
                  variant="body2"
                  component="a"
                  href={`tel:${workItem.customerMobile}`}
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {workItem.customerMobile}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>

            {/* Phone (if different from mobile) */}
            {workItem.customerPhone && workItem.customerPhone !== workItem.customerMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                  Phone
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href={`tel:${workItem.customerPhone}`}
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {workItem.customerPhone}
                </Typography>
              </Box>
            )}

            {/* Primary Address */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Primary Address
              </Typography>
              {(workItem.address || workItem.city) ? (
                <Typography variant="body2">
                  {[workItem.address, workItem.address2, workItem.city, workItem.state].filter(Boolean).join(', ')}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>

            {/* Services */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SmsIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Services
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {isLoadingServices && (
                  <Chip size="small" label="Loading..." variant="outlined" />
                )}
                {customerServices.map(service => (
                  <Chip
                    key={service.customerServiceId}
                    size="small"
                    label={service.displayName}
                    variant="outlined"
                    sx={{
                      bgcolor: 'action.hover',
                      borderColor: service.status?.colour ?? 'divider',
                      color: service.status?.colour ?? 'text.secondary',
                      opacity: 0.82,
                    }}
                  />
                ))}
                {isAdmin && dataverseCustomerId && (
                  <Tooltip title="Add service" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => setAddServiceModalOpen(true)}
                      sx={{ color: 'primary.main' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Aliases */}
            {dataverseCustomerId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LocalOfferIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                  Aliases
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {isLoadingAliases ? (
                    <CircularProgress size={14} />
                  ) : (
                    customerAliases
                      .filter(a => a.isActive)
                      .map(a => (
                        <Chip
                          key={a.aliasId}
                          label={a.alias}
                          size="small"
                          variant="outlined"
                          title={a.aliasTypeLabel}
                        />
                      ))
                  )}
                  {isAdmin && dataverseCustomerId && (
                    <Tooltip title="Add alias" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => setAddAliasModalOpen(true)}
                        sx={{ color: 'primary.main' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {/* Shortcuts */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <OpenInNewIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, width: 120, flexShrink: 0 }}>
                Shortcuts
              </Typography>
              <Box sx={shortcutGroupSx}>
                <Tooltip title="Customer Record" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!rsCustomerId}
                      onClick={() => window.open(`https://techsolvers.repairshopr.com/customers/${rsCustomerId}`, '_blank', 'noopener,noreferrer')}
                      sx={rsShortcutSx}
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {isAdmin && (
                  <Tooltip title="Edit Customer in RepairShopr" placement="top">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!rsCustomerId}
                        onClick={() => window.open(`https://techsolvers.repairshopr.com/customers/${rsCustomerId}/edit`, '_blank', 'noopener,noreferrer')}
                        sx={rsShortcutSx}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Invoices" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!rsCustomerId}
                      onClick={() => window.open(`https://techsolvers.repairshopr.com/invoices?search%5Bcustomer_id%5D=${rsCustomerId}`, '_blank', 'noopener,noreferrer')}
                      sx={rsShortcutSx}
                    >
                      <ReceiptIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Tickets" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!rsCustomerId}
                      onClick={() => window.open(`https://techsolvers.repairshopr.com/tickets?commit=Search&ticket_search%5Bquery%5D%5Ball_fields%5D=customer_id%3A${rsCustomerId}&ticket_search_id=all`, '_blank', 'noopener,noreferrer')}
                      sx={rsShortcutSx}
                    >
                      <ConfirmationNumberIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Notes (below the rows) */}
          {dataverseCustomerId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>
                Notes
              </Typography>
              {isLoadingNotes ? (
                <CircularProgress size={14} />
              ) : (
                <Typography
                  variant="body2"
                  color={customerNotes?.trim() ? 'text.primary' : 'text.secondary'}
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {customerNotes?.trim() || 'No notes'}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Right: Job log + action buttons */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Device — jobs created from a check-in carry the originating
              device(s), attached server-side (checkInDraftJobs) matched by RS
              ticket id. Sits alongside the job log / quotes / tasks. */}
          {workItem.type !== 'check-in' && (workItem.checkInDraftJobs?.length ?? 0) > 0 && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {workItem.checkInDraftJobs!.length > 1 ? 'Devices' : 'Device'}
                </Typography>
                {onOpenCheckInSession && workItem.checkInSessionGuid && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => onOpenCheckInSession(workItem.checkInSessionGuid!)}
                  >
                    Check-In
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {workItem.checkInDraftJobs!.map(renderCheckInDeviceRow)}
              </Box>
            </Box>
          )}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <JobLogPanel
              entries={displayEntries}
              isLoading={logsLoading}
              error={logsError}
              isSubmitting={isSubmitting}
              onAddLog={handleAddLog}
              onSync={hasLinkedTicket ? handleRsSync : undefined}
              isSyncing={isRsSyncing}
              customerFirstName={workItem.customerName.split(' ')[0]}
              userFirstName={userName.split(' ')[0]}
              customerPhone={workItem.customerMobile || workItem.customerPhone}
              focusDiagnosticsSignal={focusDiagnosticsSignal}
            />
          </Box>

          {/* Quotes — list quotes linked to this job, with a +
              button matching the Line Items style. */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: quotes.length > 0 ? 1 : 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Quotes{quotes.length > 0 ? ` (${quotes.length})` : ''}
              </Typography>
              {quotes.length === 0 && (
                <Typography variant="body2" color={!workItem.jobGUID ? 'text.disabled' : 'text.secondary'}>
                  {!workItem.jobGUID ? 'No linked job' : quotesLoading ? 'Loading…' : 'No quotes'}
                </Typography>
              )}
              <Tooltip title="Create quote" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateQuote();
                    }}
                    disabled={creatingQuote || !workItem.customerId}
                    sx={{ color: 'primary.main', p: 0.25 }}
                  >
                    {creatingQuote ? (
                      <CircularProgress size={14} />
                    ) : (
                      <AddIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {quotes.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {quotes.map((quote) => (
                  <Paper
                    key={quote.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      invoiceNavigate(`/quote/${quote.number}`);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Quote #{quote.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {quote.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatQuoteServiceTotal(quote)}
                      </Typography>
                      <Chip
                        label={QUOTE_TYPE_LABEL[quote.type]}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={quote.status}
                        size="small"
                        color={QUOTE_STATUS_COLOR[quote.status]}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
            {quotesError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setQuotesError(null)}>
                {quotesError}
              </Alert>
            )}
            {createQuoteError && (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                onClose={() => setCreateQuoteError(null)}
              >
                {createQuoteError}
              </Alert>
            )}
          </Box>

          {/* Task List */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: checklist ? 'pointer' : 'default' }}
              onClick={() => { if (checklist) setChecklistExpanded(prev => !prev); }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {checklist ? `Tasks (${progress.closed}/${progress.total})` : 'Task List'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!showTaskListPicker && (
                  <Tooltip title={checklist ? 'Add another task list' : 'Add task list'} placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setShowTaskListPicker(true); }}
                      sx={{ color: 'primary.main' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {checklist && (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.secondary',
                      transform: checklistExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                )}
              </Box>
            </Box>

            {showTaskListPicker && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 280 }}
                  options={taskListCatalog.taskLists}
                  getOptionLabel={(o) => o.displayName}
                  loading={isLoadingTaskLists}
                  onChange={(_, value) => { if (value) handleAttachTaskList(value); }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select a task list..." autoFocus />
                  )}
                />
                <Button size="small" onClick={() => setShowTaskListPicker(false)}>
                  Cancel
                </Button>
              </Box>
            )}

            {!checklist && !showTaskListPicker && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No task lists
              </Typography>
            )}

            {checklist && (
              <Collapse in={checklistExpanded}>
                <Box sx={{ mt: 1 }}>
                  <ChecklistPanel
                    checklist={checklist}
                    progress={progress}
                    disabled={isSaving}
                    taskDropdowns={dropdownCatalog.dropdowns}
                    onSetStatus={onSetStatus}
                    onSetNote={onSetNote}
                    onSetOption={onSetOption}
                    onRemoveTask={onRemoveTask}
                    onRestoreTask={onRestoreTask}
                    onRemoveSection={onRemoveSection}
                    onRestoreSection={onRestoreSection}
                    onAddManualTask={onAddManualTask}
                  />
                  {saveError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {saveError}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={onSave}
                      disabled={!isDirty || isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Checklist'}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            )}
          </Box>

          {/* Line Items */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setLineItemsExpanded(prev => !prev)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Line Items{lineItems.length > 0 ? ` (${lineItems.length})` : ''}
                </Typography>
                {lineItems.length > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${lineItemsTotal.toFixed(2)}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Add line item" placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setLineItemsExpanded(true); onAddLineItem(); }}
                    disabled={lineItemsSaving}
                    sx={{ color: 'primary.main' }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {hasLinkedTicket && hasUnpaidItems && (
                  <Tooltip title={createInvoiceDisabledReason ?? 'Create invoice'} placement="top">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onCreateInvoice(); }}
                        disabled={createInvoiceDisabledReason !== null}
                        sx={{ color: 'success.main' }}
                      >
                        <ReceiptIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {hasLinkedTicket && (
                  <Tooltip title="Sync RepairShopr" placement="top">
                    <span>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); wrappedOnSync(); }}
                      disabled={isSyncing}
                      sx={{ color: 'primary.main' }}
                    >
                      {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                    </IconButton>
                    </span>
                  </Tooltip>
                )}
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: 'text.secondary',
                    transform: lineItemsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Box>
            </Box>

            <Collapse in={lineItemsExpanded}>
            {createInvoiceError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setCreateInvoiceError(null)}>
                {createInvoiceError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 1 }}>
                {invoices && invoices.length > 0 && (
                  <Chip
                    size="small"
                    icon={<ReceiptLongIcon />}
                    label={`Invoice #${invoices[0].number}`}
                    color={invoicePaid ? 'success' : 'info'}
                    variant="outlined"
                    onClick={() => invoiceNavigate(`/invoice/${invoices[0].number}`)}
                    clickable
                  />
                )}
                {hasRsData && hasConvertedItems && !invoices && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={isCheckingInvoice ? <CircularProgress size={16} /> : <ReceiptLongIcon />}
                    onClick={onCheckInvoice}
                    disabled={isCheckingInvoice}
                  >
                    Check invoice
                  </Button>
                )}
                {hasLinkedTicket && (
                  <Typography variant="caption" color="text.secondary">
                    {relativeSyncRan ? `Last check: ${relativeSyncRan}` : 'Not yet synced'}
                  </Typography>
                )}
            </Box>

            {!hasRsData && hasLinkedTicket && lineItems.length === 0 && !lastSyncedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Line items haven&apos;t been synced from RepairShopr yet
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={wrappedOnSync}
                  disabled={isSyncing}
                >
                  Sync now
                </Button>
              </Box>
            )}

            {lineItems.length === 0 && !hasLinkedTicket && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No line items
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {lineItems.map((item, index) => (
                <LineItemCard
                  key={item.id}
                  index={index}
                  lineItem={item}
                  expanded={!!lineItemExpanded[item.id]}
                  canDelete={!item.converted || !invoicePaid}
                  invoicePaid={invoicePaid}
                  invoiceId={invoiceId}
                  autoFocus={item.source === 'manual' && !item.lineItemId && !!lineItemExpanded[item.id]}
                  quantityError={validationErrorItemId === item.id}
                  onExpandChange={onLineItemExpandChange}
                  onUpdate={onUpdateLineItem}
                  onDelete={onDeleteLineItem}
                  onProvisionLicense={
                    workItem.customerId && workItem.jobGUID
                      ? (id) => provisionLicense(id, workItem.customerId)
                      : undefined
                  }
                  products={products}
                  resolvedPricingProfile={resolvedPricing}
                  afterHoursContext={afterHoursContext}
                />
              ))}
            </Box>

            {lineItems.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mt: 1 }}>
                {hasMonthlyInvoiceSchedule ? (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={onSaveLineItems}
                      disabled={!lineItemsDirty || lineItemsSaving}
                    >
                      {lineItemsSaving ? 'Saving...' : 'Save Ticket'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Existing business customer — to be billed on next monthly invoice
                    </Typography>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      saveAndPayActive ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                    }
                    onClick={handleSaveAndTakePayment}
                    disabled={
                      saveAndPayActive ||
                      lineItemsSaving ||
                      isSyncing ||
                      (!lineItemsDirty && !hasPendingLineItems && !lineItems.some((li) => !li.converted))
                    }
                  >
                    {saveAndPayActive
                      ? lineItemsSaving
                        ? 'Saving...'
                        : hasPendingLineItems || isSyncing
                          ? 'Waiting for sync...'
                          : invoiceCreationPhase === 'submitting' || invoiceCreationPhase === 'accepted'
                            ? 'Creating invoice...'
                            : 'Working...'
                      : 'Take Payment'}
                  </Button>
                )}
              </Box>
            )}

            {syncError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {syncError}
              </Alert>
            )}
            {lineItemsSaveError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {lineItemsSaveError}
              </Alert>
            )}
            {saveAndPayError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSaveAndPayError(null)}>
                {saveAndPayError}
              </Alert>
            )}
            </Collapse>
          </Box>
        </Box>
      </Box>

      {/* Appointment detail */}
      {todayAppointment && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Appointment
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  {formatAppointmentDateTime(todayAppointment.startAt, todayAppointment.endAt)}
                </Typography>
                {workItem.appointmentType && (
                  <Chip label={workItem.appointmentType} size="small" variant="outlined" />
                )}
              </Box>
              {(workItem.address || workItem.city) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[workItem.address, workItem.address2, workItem.city, workItem.state]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              )}
            </Paper>
          </Box>
        </>
      )}

      {/* Check-In Session details */}
      {workItem.type === 'check-in' && (
        <>
          <Divider />
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Check-In
              </Typography>
              {onOpenCheckInSession && workItem.checkInSessionGuid && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() => onOpenCheckInSession(workItem.checkInSessionGuid!)}
                >
                  Open Session
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 1.5, gap: 0.75, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                {workItem.checkInIntakeType && (
                  <Chip
                    label={workItem.checkInIntakeType === 'in_store' ? 'In-Store' : 'Online'}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                )}
                {workItem.checkInPaymentState && (
                  <Chip
                    label={workItem.checkInPaymentState.replace(/_/g, ' ')}
                    size="small"
                    variant="outlined"
                    color={
                      workItem.checkInPaymentState === 'paid' || workItem.checkInPaymentState === 'paid_overridden'
                        ? 'success'
                        : workItem.checkInPaymentState === 'deferred'
                        ? 'info'
                        : workItem.checkInPaymentState === 'failed'
                        ? 'error'
                        : 'warning'
                    }
                  />
                )}
                {workItem.checkInRequiresAttention && (
                  <Chip label="Needs attention" size="small" color="error" />
                )}
              </Box>
            </Box>

            {workItem.checkInExpiresAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Expires {new Date(workItem.checkInExpiresAt).toLocaleString()}
              </Typography>
            )}

            {typeof workItem.checkInPreliminaryChargesTotal === 'number' && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Preliminary charges:{' '}
                <strong>
                  {workItem.checkInPreliminaryChargesTotal.toLocaleString(undefined, {
                    style: 'currency',
                    currency: 'AUD',
                  })}
                </strong>
              </Typography>
            )}

            {workItem.checkInDraftJobs && workItem.checkInDraftJobs.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                {workItem.checkInDraftJobs.map(renderCheckInDeviceRow)}
              </Box>
            )}

            {workItem.checkInRetryAvailable && workItem.checkInSessionGuid && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<SyncIcon fontSize="small" />}
                  onClick={async () => {
                    if (!workItem.checkInSessionGuid) return;
                    try {
                      const response = await fetch(
                        `/api/check-in-sessions/${workItem.checkInSessionGuid}/orchestration/retry`,
                        { method: 'POST' },
                      );
                      if (!response.ok) throw new Error(`Retry failed: ${response.status}`);
                      onWorkItemUpdated(workItem.id, { checkInRetryAvailable: false, checkInRequiresAttention: false });
                    } catch (err) {
                      console.error('[CheckIn] Retry orchestration failed:', err);
                    }
                  }}
                >
                  Retry orchestration
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Linked Items */}
      {(workItem.type === 'job' || workItem.type === 'appointment' || workItem.type === 'job-appointment') && (
        <>
          <Divider />
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setLinkedItemsExpanded(prev => !prev)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Linked Items
                </Typography>
              </Box>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: linkedItemsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>
            <Collapse in={linkedItemsExpanded}>
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Appointments (for jobs) */}
                {workItem.type === 'job' && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Appointments
                    </Typography>
                    {workItem.appointments && workItem.appointments.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {workItem.appointments.map(appt => (
                          <Paper key={appt.uuid} variant="outlined" sx={{ p: 1.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <CalendarMonthIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{appt.title}</Typography>
                                  {appt.type && (
                                    <Chip label={appt.type} size="small" variant="outlined" />
                                  )}
                                  <Chip label={appt.status} size="small" variant="outlined" />
                                </Box>
                                {appt.startAt && (
                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.25 }}>
                                    {formatAppointmentDateTime(appt.startAt, appt.endAt)}
                                  </Typography>
                                )}
                                {appt.userFullName && (
                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.25 }}>
                                    Assigned to {appt.userFullName}
                                  </Typography>
                                )}
                                {appt.notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.25, whiteSpace: 'pre-wrap' }}>
                                    {appt.notes}
                                  </Typography>
                                )}
                              </Box>
                              <Tooltip title="Unlink appointment">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setUnlinkingAppointment(appt.uuid);
                                    unlinkAppointmentFromJob(appt.uuid, workItem.guid)
                                      .then(() => {
                                        const updated = (workItem.appointments ?? []).filter(a => a.uuid !== appt.uuid);
                                        onWorkItemUpdated(workItem.id, { appointments: updated });
                                      })
                                      .catch(err => console.error('[LinkedItems] Unlink failed:', err))
                                      .finally(() => setUnlinkingAppointment(null));
                                  }}
                                  disabled={unlinkingAppointment === appt.uuid}
                                >
                                  {unlinkingAppointment === appt.uuid ? <CircularProgress size={16} /> : <LinkOffIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No linked appointments</Typography>
                    )}
                  </Box>
                )}

                {/* Linked Tickets (job → job, the ticket form's Link button) */}
                {workItem.type === 'job' && (workItem.linkedFromJobNumber || linkedTickets.length > 0 || isLoadingLinkedTickets) && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Linked Tickets
                    </Typography>
                    <LinkedTicketChips
                      parent={workItem.linkedFromJobNumber
                        ? { number: workItem.linkedFromJobNumber, title: workItem.linkedFromJobTitle }
                        : null}
                      children={linkedTickets}
                      isLoading={isLoadingLinkedTickets}
                    />
                  </Box>
                )}

                {/* Linked Job (for appointments) */}
                {(workItem.type === 'appointment' || workItem.type === 'job-appointment') && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Linked Job
                    </Typography>
                    {workItem.linkedJobGuid ? (
                      <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <ConfirmationNumberIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                            {workItem.linkedJobNumber ? `#${workItem.linkedJobNumber}` : 'Job'}
                          </Typography>
                          {workItem.linkedJobTitle && (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                              {workItem.linkedJobTitle}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Unlink job">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setUnlinkingAppointment(workItem.guid);
                              unlinkAppointmentFromJob(workItem.guid, workItem.linkedJobGuid!)
                                .then(() => {
                                  onWorkItemUpdated(workItem.id, { linkedJobGuid: undefined });
                                })
                                .catch(err => console.error('[LinkedItems] Unlink failed:', err))
                                .finally(() => setUnlinkingAppointment(null));
                            }}
                            disabled={unlinkingAppointment === workItem.guid}
                          >
                            {unlinkingAppointment === workItem.guid ? <CircularProgress size={16} /> : <LinkOffIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No linked job</Typography>
                    )}
                  </Box>
                )}

                {/* Parts — only render when this work item has parts on order */}
                {partLines && partLines.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Parts ({partLines.length})
                    </Typography>
                    <PartsPanel workitemId={workItem.jobGUID ?? workItem.guid} />
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </>
      )}

      {/* Delete button */}
      {onDelete && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={isDeleting ? <CircularProgress size={16} color="error" /> : <DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
          <Dialog open={deleteConfirmOpen} onClose={() => !isDeleting && setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete Work Item</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Delete ticket {workItem.number ?? workItem.id} for {workItem.customerName}? This will remove the RS ticket and Dataverse job.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
    {isAdmin && (
      <AddCustomerServiceModal
        open={addServiceModalOpen}
        onClose={() => setAddServiceModalOpen(false)}
        onSubmit={async (payload) => { await createService(payload); }}
        existingServiceIds={customerServices.map(s => s.serviceId)}
        customerName={customerDisplayName}
      />
    )}
    {isAdmin && (
      <AddEditAliasModal
        open={addAliasModalOpen}
        mode="add"
        editAlias={null}
        vendors={vendors}
        onClose={() => setAddAliasModalOpen(false)}
        onSubmitCreate={createAlias}
        onSubmitUpdate={async () => {}}
      />
    )}
    {invoicePromptOpen && (
      <TicketInvoicePrompt
        open={invoicePromptOpen}
        onClose={() => setInvoicePromptOpen(false)}
        lineItems={workItem.rsLineItems ?? []}
        rsCustomerId={rsCustomerId}
        rsTicketId={Number(workItem.id) || 0}
        dvJobGuid={jobGuid}
      />
    )}
    <TaskListContextPromptDialog
      open={!!pendingTaskListAttach}
      taskListLabel={pendingTaskListAttach?.def.displayName ?? ''}
      missingFieldValueKeys={pendingTaskListAttach?.missing.fieldValueKeys ?? []}
      missingCustomerContextKeys={pendingTaskListAttach?.missing.customerContextKeys ?? []}
      onCancel={() => setPendingTaskListAttach(null)}
      onSubmit={handleTaskListContextSubmit}
    />
    <NoDiagnosticsQuoteDialog
      open={noDiagnosticsPromptOpen}
      onCancel={handleDeclineQuoteWithoutDiagnostics}
      onContinue={handleConfirmQuoteWithoutDiagnostics}
    />
    {correctingSerialJob && workItem.checkInSessionGuid && (
      <CorrectSerialDialog
        open
        sessionId={workItem.checkInSessionGuid}
        draftJob={correctingSerialJob}
        onClose={() => setCorrectingSerialJobId(null)}
        onSaved={refreshCheckInSession}
      />
    )}
    </>
  );
};
